import { formatRevenueValue } from "@/components/pages/analytics/front-desk/components/overview/overviewKpiMappers";
import { formatRevenueCurrency } from "@/components/pages/analytics/front-desk/components/revenue/revenueCurrency";
import type { MetricCardTrend } from "@/components/pages/analytics/front-desk/components/shared/MetricCard";
import type { TenantRevenueTableRow } from "@/types/analytics-tenant";
import type { AdminHotelMetricRow } from "../types/adminHotelMetricTable";

const DELTA_SUFFIX = " from previous period";

function percentChange(current: number, previous: number): number {
  if (current === previous) {
    return 0;
  }

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return ((current - previous) / previous) * 100;
}

function trendFromChange(changePct: number): MetricCardTrend {
  if (changePct === 0) return "neutral";
  return changePct > 0 ? "positive" : "negative";
}

function formatPercentDelta(changePct: number): {
  delta: string;
  trend: MetricCardTrend;
} {
  const sign = changePct > 0 ? "+" : "";
  const formatted = changePct.toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });
  return {
    delta: `${sign}${formatted}%${DELTA_SUFFIX}`,
    trend: trendFromChange(changePct),
  };
}

function toMajorUnits(amount: number, divisor: number): number {
  return amount / (divisor || 1);
}

export function mapTenantRevenueRowsToMetricRowsWithComparison(input: {
  currentRows: TenantRevenueTableRow[];
  previousRows: TenantRevenueTableRow[];
  hotelLogosById?: Record<string, string | null>;
}): AdminHotelMetricRow[] {
  const previousByHotelId = new Map(
    input.previousRows.map((row) => [row.hotelId, row]),
  );

  return input.currentRows.map((row) => {
    const divisor = row.amountDivisor || 1;
    const currency = row.currency ?? "EUR";
    const currentTotalRevenue = toMajorUnits(row.totalRevenue, divisor);
    const currentAvgCheck = toMajorUnits(
      row.avgCheckPerReservation ?? 0,
      divisor,
    );

    const previousRow = previousByHotelId.get(row.hotelId);
    const previousDivisor = previousRow?.amountDivisor || 1;
    const previousTotalRevenue = toMajorUnits(
      previousRow?.totalRevenue ?? 0,
      previousDivisor,
    );
    const previousAvgCheck = toMajorUnits(
      previousRow?.avgCheckPerReservation ?? 0,
      previousDivisor,
    );

    const totalRevenueDelta = formatPercentDelta(
      percentChange(currentTotalRevenue, previousTotalRevenue),
    );
    const avgCheckDelta = formatPercentDelta(
      percentChange(currentAvgCheck, previousAvgCheck),
    );

    return {
      id: row.hotelId,
      hotelBrand: row.hotelName,
      hotelLogoId: input.hotelLogosById?.[row.hotelId] ?? null,
      metrics: {
        totalRevenue: {
          value: formatRevenueValue(currentTotalRevenue),
          delta: totalRevenueDelta.delta,
          trend: totalRevenueDelta.trend,
        },
        avgCheckPerReservation: {
          value: formatRevenueCurrency(currentAvgCheck, currency, {
            maximumFractionDigits: 2,
          }),
          delta: avgCheckDelta.delta,
          trend: avgCheckDelta.trend,
        },
      },
    };
  });
}
